import {Engine} from "platform/engine/Engine";
import {XhrUtil} from "core/util/XhrUtil";
import {
    OptInLessonPayload,
    SetAcademyVideoPayload,
    SetLDAcademyConfiguration,
    SetLessons
} from "core/redux/lessons/LessonsReduxActions";
import {HttpReject} from "platform/network/http/Http";
import Utils from "platform/util/Utils";
import {LanguageUtil} from "platform/util/LanguageUtil";
import {LangCode} from "platform/enum/LangCode";
import {AccountState} from "core/state/AccountState";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import {TSMap} from "typescript-map";
import {
    GetUIConfigurationResponse,
    Lesson,
    Tutorial,
    TutorialCategory
} from "protocol/config/GetUIConfigurationResponse";
import {GetUIConfigurationRequest} from "protocol/config/GetUIConfigurationRequest";
import {SetUrls} from "platform/redux/core/CoreActions";
import {UrlType} from "platform/enum/UrlType";
import {
    InsertClientPreference,
    SetAccountSettingsConfig,
    SetCallMeEnabled,
    SetShareInformationEnabled
} from "core/redux/settings/SettingsReduxActions";
import {ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import {TranslationKey} from "enum/TranslationKey";
import {DoRegisterUserActivity} from "core/redux/account/AccountReduxActions";
import {UserActivityType} from "enum/UserActivityType";
import {SetAuthStatusPayload} from "core/redux/auth/AuthReduxActions";
import {StoreState} from "core/redux/StoreState";
import {Configuration} from "core/configuration/Configuration";
import {
    SetLDPlatformPropsPayload,
    SetMenuRouteAllowed,
    SetMenuToolAllowed,
    ShowAppCuesFlowPayload
} from "core/redux/app/AppReduxActions";
import {StorageKey} from "enum/StorageKey";
import {Preference} from "core/util/Preference";
import {RouteType} from "core/router/Routes";
import ToolsMenuType from "enum/ToolsMenuType";
import {EnvType} from "platform/enum/EnvType";

export default class LessonsEngine extends Engine {

    private static _instance: LessonsEngine;

    public static instance(): LessonsEngine {
        return this._instance || (this._instance = new this());
    }

    public doSetAuthStatus = async (payload: SetAuthStatusPayload) => {
        const {connected, authenticated, accountType} = Platform.reduxState<StoreState>().auth;
        if (connected) {
            if ((!authenticated && payload.authStatus.authenticated) || (accountType && payload.authStatus.accountType && accountType !== payload.authStatus.accountType)) {
                this.doFetchUIConfiguration().catch(() => {
                });
            }
        }
    }

    public onSetLDPlatformProps = ({props, walkThroughGroups, walkThrough, academyVideoGroups, academyVideos}: SetLDPlatformPropsPayload): void => {
        if ((Platform.environment().type() === EnvType.Web && Utils.isMapNotEmpty(walkThrough)) || Utils.isMapNotEmpty(academyVideos)) {
            Platform.dispatch(SetMenuRouteAllowed({
                routeType: RouteType.Lessons,
                allowed: true
            }));
            Platform.dispatch(SetMenuToolAllowed({
                toolType: ToolsMenuType.Lessons,
                allowed: true
            }));
        }
        const supportZendesk = props?.keys?.SupportZendesk || {};
        Platform.dispatch(SetLDAcademyConfiguration({
            walkThroughGroups,
            walkThrough,
            academyVideoGroups,
            academyVideos,
            supportZendesk
        }));
    }

    public doOptInLesson = async (payload: OptInLessonPayload) => {
        const {Id} = payload.lesson;
        this._logger.debug("Opt in lesson: " + Id);
        const request: any = {activityType: 106};
        const answer: [HttpReject, any] = await Utils.to(this.sendToLessonsService(request, "RegisterActivity"));
        if (answer[0]) {
            this._logger.debug("Failed opt in lesson");
        }
    }

    public doSetAcademyVideo = async (payload: SetAcademyVideoPayload) => {
        const preference: Preference<string[]> = Platform.reduxState<StoreState>().settings.preferences.get(StorageKey.AcademyWatchedVideos);
        const ids: string[] = preference?.value || [];
        const id: string = payload.video?.videoId;
        if (id && (Utils.isNull(preference) || Utils.isArrayEmpty(ids) || ids.indexOf(id) < 0)) {
            Platform.dispatch(InsertClientPreference({
                key: StorageKey.AcademyWatchedVideos,
                preference: Preference.Of([...ids, id])
            }));
        }
    }

    public doShowAppCuesFlow = async (payload: ShowAppCuesFlowPayload) => {
        const preference: Preference<string[]> = Platform.reduxState<StoreState>().settings.preferences.get(StorageKey.AcademyPassedWalkThroughS);
        const ids: string[] = preference?.value || [];
        if (payload.id && (Utils.isNull(preference) || Utils.isArrayEmpty(ids) || ids.indexOf(payload.id) < 0)) {
            Platform.dispatch(InsertClientPreference({
                key: StorageKey.AcademyPassedWalkThroughS,
                preference: Preference.Of([...ids, payload.id])
            }));
        }
    }

    public doFetchUIConfiguration = async () => {
        this._logger.debug("Fetch lessons");
        const langCode: LangCode = LanguageUtil.languageCode();
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const request: GetUIConfigurationRequest = {languageCode: langCode, userId: accountState.userId};
        const answer: [HttpReject, GetUIConfigurationResponse] = await Utils.to(this.sendToWebProfitService(request, "GetUIConfiguration"));
        if (answer[0]) {
            this._logger.debug("Failed fetch lessons");
        } else {
            const categories: TutorialCategory[] = [];
            const lessons: TSMap<number, Lesson[]> = new TSMap<number, Lesson[]>();
            const {
                Expiration,
                Tutorials,
                EbookUrl,
                AutochartistUrl,
                EconomicCalendarUrl,
                TermsAndConditionsUrl,
                AnnualStatementYears,
                ShowShareInformation,
                CallMeEnabled
            } = answer[1] || {};
            if (Tutorials && Tutorials.TutorialsDictionary) {
                Object.keys(Tutorials.TutorialsDictionary).forEach((key: string) => {
                    const tutorials: Tutorial[] = Tutorials.TutorialsDictionary[key];
                    if (Utils.isArrayNotEmpty(tutorials)) {
                        tutorials.forEach((tutorial: Tutorial) => {
                            if (tutorial.Allowed && tutorial.Tutorial) {
                                const category: TutorialCategory = tutorial.Tutorial.TutorialCategory;
                                if (category && Utils.isNotNull(category.TutorialCategoryId)) {
                                    let groupLessons: Lesson[] = lessons.get(category.TutorialCategoryId);
                                    if (!groupLessons) {
                                        categories.push(category);
                                        lessons.set(category.TutorialCategoryId, groupLessons = []);
                                    }
                                    groupLessons.push(tutorial.Tutorial);
                                }
                            }
                        });
                    }
                });
                categories.sort((c1: TutorialCategory, c2: TutorialCategory) => Utils.compareNumber(c1.TutorialCategoryId, c2.TutorialCategoryId));
                lessons.forEach((ls: Lesson[]) => {
                    ls.sort((l1: Lesson, l2: Lesson) => Utils.compareNumber(l1.DisplayOrder, l2.DisplayOrder));
                });
            }
            Platform.dispatch(SetLessons({
                categories,
                lessons,
                LearningCenterExpiration: Expiration,
                EbookUrl
            }));
            const {brand, theme} = Platform.reduxState<StoreState>().core;
            const domain: string = Platform.config<Configuration>().amazonOrigin;
            const cssFileAutoChartist: string = `${domain}css/autoChartist-${brand}-${theme}.css`;
            const acUrlFirstPart: string = AutochartistUrl.substring(0, AutochartistUrl.indexOf("%26css%3d"));
            const acUrlSecondPart: string = AutochartistUrl.substring(AutochartistUrl.indexOf("%26chart_style%3d"));
            const autoChartistUrl: string = `${acUrlFirstPart}%26css%3d${encodeURIComponent(cssFileAutoChartist)}${acUrlSecondPart}`;
            Platform.dispatch(SetUrls({
                urls: [
                    {
                        type: UrlType.AutochartistUrl, url: autoChartistUrl
                    },
                    {
                        type: UrlType.EconomicCalendarUrl, url: EconomicCalendarUrl
                    },
                    {
                        type: UrlType.CreditPromoTcUrl, url: TermsAndConditionsUrl
                    }
                ]
            }));
            Platform.dispatch(SetAccountSettingsConfig({
                AnnualStatementYears: AnnualStatementYears || []
            }));
            Platform.dispatch(SetShareInformationEnabled({enabled: ShowShareInformation}));
            Platform.dispatch(SetCallMeEnabled({enabled: CallMeEnabled}));
            if (CallMeEnabled) {
                Platform.dispatch(SetMenuToolAllowed({
                    toolType: ToolsMenuType.LiveChat,
                    allowed: false
                }));
                Platform.dispatch(SetMenuToolAllowed({
                    toolType: ToolsMenuType.Support,
                    allowed: true
                }));
            }
        }
    }

    public doConfirmRequestEnableLearningCenter = async () => {
        Platform.dispatch(ShowPopup({
            popup: {
                type: PopupType.CONFIRM,
                icon: PopupIconType.CONFIRM,
                title: {trKey: TranslationKey.confirm},
                message: {
                    trKey: TranslationKey.lessonsEnableConfirmation
                },
                actions: [
                    {
                        type: PopupActionType.OK,
                        action: () => {
                            this.doRequestEnableLessons();
                        }
                    },
                    {
                        type: PopupActionType.CANCEL
                    }
                ]
            }
        }));
    }

    public doRequestEnableEbook = async () => {
        Platform.dispatch(DoRegisterUserActivity({
            userActivity: UserActivityType.EBookNotAllowed
        }));
        this.notifyManagerWillContact();
    }

    public doRequestExtendTutorials = async () => {
        Platform.dispatch(DoRegisterUserActivity({
            userActivity: UserActivityType.TutorialsExtend
        }));
        this.notifyManagerWillContact();
    }

    public doRequestEnableLessons = async () => {
        Platform.dispatch(DoRegisterUserActivity({
            userActivity: UserActivityType.RetentionTutorialsNotAllowed
        }));
        this.notifyManagerWillContact();
    }

    private notifyManagerWillContact = () => {
        Platform.dispatch(ShowPopup({
            popup: {
                type: PopupType.INFO,
                icon: PopupIconType.INFO,
                message: {
                    trKey: TranslationKey.lessonsFreeEbookManagerContact
                },
                actions: [{type: PopupActionType.OK}]
            }
        }));
    }

    private sendToLessonsService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "LearningCenter/Tutorials/" + path);
    }

    private sendToWebProfitService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/WebProfitClientService.svc/json/" + path, null, true);
    }
}
